/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Manages JWT tokens, user data, and tenant information.
 *
 * Uses dual-token system (15-minute access tokens + 7-day refresh tokens).
 *
 * Listens for 'auth:force-logout' events from the API client interceptor
 * to perform controlled logouts (clearing React state and timers) instead
 * of hard page redirects.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { LoginRequest, LoginStep1Response, LoginResponse, AccessTokenPayload, ActiveRole, ExpiredRoleInfo } from '../types/auth.types';
import type { UserSanitized, RoleAssignment } from '../types/user.types';
import type { TenantSanitized } from '../types/tenant.types';
import { RoleType } from '../types/role.types';
import {
  parseJWTUnsafe,
  saveTokenPair,
  getAccessToken,
  getRefreshToken,
  clearAllTokens,
  isTokenExpired,
  getTimeUntilExpiry,
  saveAccessToken,
} from '../lib/utils/token';
import { authService } from '../api/auth.service';
import {
  AUTH_FORCE_LOGOUT_EVENT,
  AUTH_TOKEN_REFRESHED_EVENT,
  AUTH_SESSION_REVOKED_EVENT,
  authSyncChannel,
  performTokenRefresh,
} from '../api/client';
import { STORAGE_KEYS, TOKEN_EXPIRY_BUFFER } from '../lib/constants';

// ============================================================================
// TYPES
// ============================================================================

interface AuthContextValue {
  // State
  user: UserSanitized | null;
  tenant: TenantSanitized | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Login 2FA state
  pendingLogin2FA: {
    tenantCode: string;
    email: string;
    codeId: string;
    destination: string;
    expiresIn: number;
    attemptsRemaining: number;
  } | null;

  // Role session state
  activeRole: ActiveRole | null;
  isRoleSessionActive: boolean;
  roleSessionRemainingSeconds: number | null;
  pendingRoleActivation2FA: {
    tenantRoleId: string;
    durationHours?: number;
    codeId: string;
    destination: string;
    expiresIn: number;
    attemptsRemaining: number;
  } | null;

  // Role session expired state (for modal)
  expiredRoleInfo: ExpiredRoleInfo | null;

  // Computed values
  availableRoles: RoleAssignment[]; // User's assigned tenant-type roles

  // Login methods (2-step with 2FA)
  loginStep1: (credentials: LoginRequest) => Promise<LoginStep1Response>;
  verifyLogin: (code: string) => Promise<void>;
  resendLoginCode: () => Promise<LoginStep1Response>;
  cancelLogin2FA: () => void;

  // SSO login
  completeSSOLogin: (response: LoginResponse) => void;

  // Session methods
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  updateUser: (updatedUser: UserSanitized) => void;
  updateTenant: (updatedTenant: TenantSanitized) => void;

  // Role session methods
  activateRole: (roleId: string, durationHours?: number, verificationCodeId?: string, verificationCode?: string) => Promise<void>;
  deactivateRole: () => Promise<void>;
  verifyRoleActivation2FA: (code: string) => Promise<void>;
  cancelRoleActivation2FA: () => void;

  // Role session expired methods
  dismissRoleSessionExpired: () => Promise<void>;
  extendRoleSession: () => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserSanitized | null>(null);
  const [tenant, setTenant] = useState<TenantSanitized | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeRole, setActiveRole] = useState<ActiveRole | null>(null);
  const [expiredRoleInfo, setExpiredRoleInfo] = useState<ExpiredRoleInfo | null>(null);
  const [pendingLogin2FA, setPendingLogin2FA] = useState<{
    tenantCode: string;
    email: string;
    codeId: string;
    destination: string;
    expiresIn: number;
    attemptsRemaining: number;
  } | null>(null);
  const [pendingRoleActivation2FA, setPendingRoleActivation2FA] = useState<{
    tenantRoleId: string;
    durationHours?: number;
    codeId: string;
    destination: string;
    expiresIn: number;
    attemptsRemaining: number;
  } | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const roleSessionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // LOCAL LOGOUT (no server call - used for force-logout from interceptor)
  // ============================================================================

  /**
   * Perform local-only logout: clear timers, state, and localStorage.
   * Does NOT call server logout endpoint (session is already invalid).
   * ProtectedRoute will automatically redirect to /login when isAuthenticated becomes false.
   */
  const performLocalLogout = useCallback(() => {
    // Clear timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (roleSessionTimerRef.current) {
      clearTimeout(roleSessionTimerRef.current);
      roleSessionTimerRef.current = null;
    }

    // Clear localStorage
    clearAllTokens();
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.TENANT_DATA);

    // Clear React state - this makes isAuthenticated false,
    // causing ProtectedRoute to redirect to /login naturally
    setToken(null);
    setUser(null);
    setTenant(null);
    setActiveRole(null);
    setExpiredRoleInfo(null);
    setPendingLogin2FA(null);
    setPendingRoleActivation2FA(null);

    console.log('[Auth] Local logout completed (force-logout from interceptor)');
  }, []);

  // ============================================================================
  // ROLE SESSION HELPERS (must be defined before initializeAuth)
  // ============================================================================

  /**
   * Extract active role from access token
   */
  const extractActiveRoleFromToken = useCallback((accessToken: string): ActiveRole | null => {
    try {
      const payload = parseJWTUnsafe(accessToken) as AccessTokenPayload | null;
      if (payload?.active_role) {
        const expiresAt = new Date(payload.active_role.expires_at);
        if (expiresAt > new Date()) {
          return payload.active_role;
        }
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Schedule role session expiration.
   * When the timer fires, shows the expiry modal instead of silently clearing state.
   * The role info is captured at scheduling time to avoid stale closure issues.
   */
  const scheduleRoleSessionExpiry = useCallback((role: ActiveRole) => {
    // Clear existing timer
    if (roleSessionTimerRef.current) {
      clearTimeout(roleSessionTimerRef.current);
      roleSessionTimerRef.current = null;
    }

    const expiresAtDate = new Date(role.expires_at);
    const now = new Date();
    const timeUntilExpiry = expiresAtDate.getTime() - now.getTime();

    if (timeUntilExpiry > 0) {
      console.log(`[Auth] Scheduling role session expiry in ${Math.floor(timeUntilExpiry / 1000)} seconds`);

      roleSessionTimerRef.current = setTimeout(() => {
        console.log('[Auth] Role session expired, showing expiry modal');

        // Store role info for the modal before clearing active role
        setExpiredRoleInfo({
          roleId: role.role_id,
          roleCode: role.role_code,
          roleName: role.role_name,
        });

        // Clear active role - UI updates immediately (sidebar, header)
        setActiveRole(null);
      }, timeUntilExpiry);
    }
  }, []);

  // ============================================================================
  // TOKEN REFRESH LOGIC
  // ============================================================================

  /**
   * Schedule automatic token refresh before expiry
   * Refresh 1 minute before access token expiry
   */
  const scheduleTokenRefresh = useCallback((accessToken: string) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const timeUntilExpiry = getTimeUntilExpiry(accessToken);
    if (timeUntilExpiry <= 0) {
      console.log('[Auth] Token already expired');
      return;
    }

    // Calculate time until refresh
    const timeUntilRefresh = Math.max(0, timeUntilExpiry - TOKEN_EXPIRY_BUFFER);

    if (timeUntilRefresh > 0) {
      console.log(`[Auth] Scheduling token refresh in ${timeUntilRefresh} seconds`);

      refreshTimerRef.current = setTimeout(async () => {
        console.log('[Auth] Token expiring soon, attempting refresh...');

        // Use the shared, self-gating performTokenRefresh so this timer and any
        // concurrent interceptor refresh share a single /auth/refresh HTTP call.
        const newToken = await performTokenRefresh();
        if (newToken) {
          setToken(newToken);
          // Schedule next refresh
          scheduleTokenRefresh(newToken);
          console.log('[Auth] Token refresh successful');
        } else {
          console.error('[Auth] Token refresh failed from timer');

          // Check if refresh token is still available
          // If not, force local logout immediately (don't wait for next API call)
          const refreshToken = getRefreshToken();
          if (!refreshToken) {
            console.log('[Auth] Refresh token expired, performing local logout');
            performLocalLogout();
          }
          // Otherwise, let the next API call's 401 handler deal with it
        }
      }, timeUntilRefresh * 1000);
    }
  }, [performLocalLogout]);

  /**
   * Initialize auth state from localStorage on mount
   */
  const initializeAuth = useCallback(async () => {
    try {
      // Get current access token
      const storedToken = getAccessToken();

      if (!storedToken) {
        // Check if we have a refresh token to get new access token
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          console.log('[Auth] Access token expired but refresh token available, attempting immediate refresh...');

          try {
            // Immediately refresh the token instead of waiting for first API call.
            // Uses the shared self-gating function so concurrent tabs/calls don't
            // double-refresh with the same refresh token.
            const newAccessToken = await performTokenRefresh();
            if (!newAccessToken) {
              throw new Error('Token refresh failed');
            }

            // Restore user/tenant data from localStorage
            const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
            const storedTenantData = localStorage.getItem(STORAGE_KEYS.TENANT_DATA);

            if (storedUserData && storedTenantData) {
              const parsedUser = JSON.parse(storedUserData) as UserSanitized;
              const parsedTenant = JSON.parse(storedTenantData) as TenantSanitized;
              setUser(parsedUser);
              setTenant(parsedTenant);
              setToken(newAccessToken);

              // Schedule next token refresh
              scheduleTokenRefresh(newAccessToken);

              console.log('[Auth] Token refresh successful on init, user restored:', {
                userId: parsedUser.userId,
                tenantCode: parsedTenant.tenantCode,
              });
            }
            setIsLoading(false);
            return;
          } catch (error) {
            console.error('[Auth] Failed to refresh token on init:', error);
            // Refresh failed - clear everything and force re-login
            clearAllTokens();
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            localStorage.removeItem(STORAGE_KEYS.TENANT_DATA);
          }
        }
        setIsLoading(false);
        return;
      }

      // Check if token is expired
      if (isTokenExpired(storedToken)) {
        console.log('[Auth] Token expired, clearing auth state');
        clearAllTokens();
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(STORAGE_KEYS.TENANT_DATA);
        setIsLoading(false);
        return;
      }

      // Try to restore full user and tenant objects from localStorage
      const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      const storedTenantData = localStorage.getItem(STORAGE_KEYS.TENANT_DATA);

      if (storedUserData && storedTenantData) {
        try {
          const parsedUser = JSON.parse(storedUserData) as UserSanitized;
          const parsedTenant = JSON.parse(storedTenantData) as TenantSanitized;

          // Set auth state from stored complete objects
          setToken(storedToken);
          setUser(parsedUser);
          setTenant(parsedTenant);

          // Extract and set active role if present in token
          const activeRoleFromToken = extractActiveRoleFromToken(storedToken);
          if (activeRoleFromToken) {
            setActiveRole(activeRoleFromToken);
            scheduleRoleSessionExpiry(activeRoleFromToken);
          }

          // Schedule token refresh
          scheduleTokenRefresh(storedToken);

          console.log('[Auth] Initialized from localStorage with full user data:', {
            userId: parsedUser.userId,
            tenantCode: parsedTenant.tenantCode,
            hasRoles: parsedUser.roles?.length > 0,
            hasActiveRole: !!activeRoleFromToken,
          });

          setIsLoading(false);
          return;
        } catch {
          console.warn('[Auth] Failed to parse stored user/tenant data, falling back to JWT reconstruction');
        }
      }

      // Fallback: Parse JWT to extract user/tenant info
      const rawPayload = parseJWTUnsafe(storedToken) as AccessTokenPayload | null;
      if (!rawPayload) {
        console.error('[Auth] Failed to parse JWT, clearing auth state');
        clearAllTokens();
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(STORAGE_KEYS.TENANT_DATA);
        setIsLoading(false);
        return;
      }

      // Reconstruct user and tenant from JWT payload
      const reconstructedUser: UserSanitized = {
        userId: rawPayload.sub,
        tenantId: rawPayload.tenant_id,
        email: rawPayload.email,
        firstName: rawPayload.name.split(' ')[0] || '',
        lastName: rawPayload.name.split(' ').slice(1).join(' ') || '',
        status: 'active' as const,
        roles: [], // Cannot reconstruct from JWT - will be empty until next login
        groups: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const reconstructedTenant: TenantSanitized = {
        tenantId: rawPayload.tenant_id,
        tenantCode: rawPayload.tenant_code,
        tenantName: '',
        adminEmail: '',
        kmsKeyId: '',
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Set auth state
      setToken(storedToken);
      setUser(reconstructedUser);
      setTenant(reconstructedTenant);

      // Extract and set active role if present in token
      const activeRoleFromToken = extractActiveRoleFromToken(storedToken);
      if (activeRoleFromToken) {
        setActiveRole(activeRoleFromToken);
        scheduleRoleSessionExpiry(activeRoleFromToken);
      }

      // Schedule token refresh
      scheduleTokenRefresh(storedToken);

      console.log('[Auth] Initialized from JWT (fallback):', {
        userId: rawPayload.sub,
        tenantCode: rawPayload.tenant_code,
        hasActiveRole: !!activeRoleFromToken,
      });
    } catch (error) {
      console.error('[Auth] Failed to initialize auth:', error);
      clearAllTokens();
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.TENANT_DATA);
    } finally {
      setIsLoading(false);
    }
  }, [scheduleTokenRefresh, extractActiveRoleFromToken, scheduleRoleSessionExpiry]);

  /**
   * Complete login after 2FA verification
   * Sets auth state with the login response
   */
  const completeLogin = useCallback(
    (response: LoginResponse): void => {
      // Save both tokens
      saveTokenPair(response.accessToken, response.refreshToken);

      // Save full user and tenant objects to localStorage
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      localStorage.setItem(STORAGE_KEYS.TENANT_DATA, JSON.stringify(response.tenant));

      // Update state
      setToken(response.accessToken);
      setUser(response.user);
      setTenant(response.tenant);

      // Clear pending 2FA state
      setPendingLogin2FA(null);

      // Schedule token refresh
      scheduleTokenRefresh(response.accessToken);

      console.log('[Auth] Login successful:', {
        userId: response.user.userId,
        tenantCode: response.tenant.tenantCode,
        roles: response.user.roles?.length || 0,
        accessExpiresIn: response.expiresIn,
        refreshExpiresIn: response.refreshExpiresIn,
      });
    },
    [scheduleTokenRefresh]
  );

  /**
   * Login Step 1: Verify credentials and send 2FA code
   * Returns 2FA challenge with codeId
   */
  const loginStep1 = useCallback(
    async (credentials: LoginRequest): Promise<LoginStep1Response> => {
      try {
        const response = await authService.loginStep1(credentials);

        // Store pending 2FA state
        setPendingLogin2FA({
          tenantCode: credentials.tenantCode,
          email: credentials.email,
          codeId: response.codeId,
          destination: response.destination,
          expiresIn: response.expiresIn,
          attemptsRemaining: response.attemptsRemaining,
        });

        console.log('[Auth] Login step 1 complete, 2FA code sent:', {
          destination: response.destination,
          expiresIn: response.expiresIn,
        });

        return response;
      } catch (error) {
        console.error('[Auth] Login step 1 failed:', error);
        throw error;
      }
    },
    []
  );

  /**
   * Login Step 2: Verify 2FA code and complete login
   * Requires pending 2FA state from loginStep1
   */
  const verifyLogin = useCallback(
    async (code: string): Promise<void> => {
      if (!pendingLogin2FA) {
        throw new Error('No pending login 2FA');
      }

      try {
        const response = await authService.verifyLogin({
          tenantCode: pendingLogin2FA.tenantCode,
          email: pendingLogin2FA.email,
          codeId: pendingLogin2FA.codeId,
          code,
        });

        completeLogin(response);
      } catch (error) {
        console.error('[Auth] Login verification failed:', error);
        throw error;
      }
    },
    [pendingLogin2FA, completeLogin]
  );

  /**
   * Resend 2FA code for login
   * Requires pending 2FA state from loginStep1
   */
  const resendLoginCode = useCallback(
    async (): Promise<LoginStep1Response> => {
      if (!pendingLogin2FA) {
        throw new Error('No pending login 2FA');
      }

      try {
        const response = await authService.resendLoginCode({
          tenantCode: pendingLogin2FA.tenantCode,
          email: pendingLogin2FA.email,
        });

        // Update pending 2FA state with new code info
        setPendingLogin2FA({
          ...pendingLogin2FA,
          codeId: response.codeId,
          expiresIn: response.expiresIn,
          attemptsRemaining: response.attemptsRemaining,
        });

        console.log('[Auth] Login 2FA code resent:', {
          destination: response.destination,
          expiresIn: response.expiresIn,
        });

        return response;
      } catch (error) {
        console.error('[Auth] Resend login code failed:', error);
        throw error;
      }
    },
    [pendingLogin2FA]
  );

  /**
   * Cancel pending login 2FA
   */
  const cancelLogin2FA = useCallback(() => {
    setPendingLogin2FA(null);
  }, []);

  /**
   * Logout user
   * Revokes session on server and clears local storage
   */
  const logout = useCallback(async () => {
    // Clear timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (roleSessionTimerRef.current) {
      clearTimeout(roleSessionTimerRef.current);
      roleSessionTimerRef.current = null;
    }

    try {
      // Call server logout (revokes session)
      await authService.logout();
      // Broadcast to other tabs so they also log out
      authSyncChannel?.postMessage({ type: AUTH_SESSION_REVOKED_EVENT });
    } catch (error) {
      // Ignore errors - local cleanup happens anyway
      console.warn('[Auth] Server logout failed:', error);
    }

    // Clear state
    setToken(null);
    setUser(null);
    setTenant(null);
    setActiveRole(null);
    setExpiredRoleInfo(null);

    console.log('[Auth] Logout successful');
  }, []);

  /**
   * Refresh authentication
   * Uses refresh token to get new access token
   */
  const refreshAuth = useCallback(async () => {
    const newToken = await performTokenRefresh();
    if (!newToken) {
      const error = new Error('Token refresh failed');
      console.error('[Auth] Failed to refresh auth:', error);
      await logout();
      throw error;
    }

    setToken(newToken);

    // Schedule next refresh
    scheduleTokenRefresh(newToken);

    console.log('[Auth] Token refresh successful');
  }, [logout, scheduleTokenRefresh]);

  /**
   * Update user data in state and localStorage
   */
  const updateUser = useCallback((updatedUser: UserSanitized) => {
    setUser(updatedUser);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));

    console.log('[Auth] User data updated:', {
      userId: updatedUser.userId,
      hasFavorites: !!updatedUser.favoriteApplications?.length,
    });
  }, []);

  /**
   * Update tenant data in state and localStorage
   */
  const updateTenant = useCallback((updatedTenant: TenantSanitized) => {
    setTenant(updatedTenant);
    localStorage.setItem(STORAGE_KEYS.TENANT_DATA, JSON.stringify(updatedTenant));

    console.log('[Auth] Tenant data updated:', {
      tenantId: updatedTenant.tenantId,
      tenantCode: updatedTenant.tenantCode,
    });
  }, []);

  // ============================================================================
  // ROLE SESSION METHODS
  // ============================================================================

  /**
   * Activate a tenant role session
   * @param roleId - The ID of the tenant role to activate
   * @param durationHours - Desired session duration in hours (1-8, defaults to tenant setting)
   * @param verificationCodeId - Optional verification code ID for 2FA
   * @param verificationCode - Optional verification code for 2FA
   */
  const activateRole = useCallback(async (roleId: string, durationHours?: number, verificationCodeId?: string, verificationCode?: string) => {
    if (!tenant) {
      throw new Error('No tenant context');
    }

    // Check if user has this tenant-type role assigned
    const hasRole = user?.roles?.some((r) => r.roleId === roleId && r.type === RoleType.TENANT);
    if (!hasRole) {
      throw new Error('You do not have this role assigned');
    }

    try {
      const response = await authService.activateRoleSession(tenant.tenantId, roleId, durationHours, verificationCodeId, verificationCode);

      // Check if 2FA is required
      if ('twoFactorRequired' in response && response.twoFactorRequired) {
        // Store 2FA state for modal (preserve durationHours for after 2FA)
        setPendingRoleActivation2FA({
          tenantRoleId: roleId,
          durationHours,
          codeId: response.codeId,
          destination: response.destination,
          expiresIn: response.expiresIn,
          attemptsRemaining: response.attemptsRemaining,
        });
        console.log('[Auth] 2FA required for role activation:', {
          roleId,
          method: response.method,
          destination: response.destination,
        });
        return;
      }

      // Successful activation - clear any pending 2FA state and expired role info
      setPendingRoleActivation2FA(null);
      setExpiredRoleInfo(null);

      // Update access token
      saveAccessToken(response.accessToken);
      setToken(response.accessToken);

      // Update active role state
      const newActiveRole: ActiveRole = {
        role_id: response.roleSession.roleId,
        role_code: response.roleSession.roleCode,
        role_name: response.roleSession.roleName,
        permissions: response.roleSession.permissions,
        expires_at: response.roleSession.expiresAt,
      };
      setActiveRole(newActiveRole);

      // Schedule expiry with full role info
      scheduleRoleSessionExpiry(newActiveRole);

      // Schedule token refresh with the new access token
      scheduleTokenRefresh(response.accessToken);

      console.log('[Auth] Role session activated:', {
        roleId: response.roleSession.roleId,
        roleCode: response.roleSession.roleCode,
        expiresAt: response.roleSession.expiresAt,
        durationSeconds: response.roleSession.durationSeconds,
      });
    } catch (error) {
      console.error('[Auth] Failed to activate role session:', error);
      throw error;
    }
  }, [tenant, user, scheduleRoleSessionExpiry, scheduleTokenRefresh]);

  /**
   * Deactivate current role session
   */
  const deactivateRole = useCallback(async () => {
    if (!tenant) {
      throw new Error('No tenant context');
    }

    // Clear timer
    if (roleSessionTimerRef.current) {
      clearTimeout(roleSessionTimerRef.current);
      roleSessionTimerRef.current = null;
    }

    try {
      const response = await authService.deactivateRoleSession(tenant.tenantId);

      // Update access token
      saveAccessToken(response.accessToken);
      setToken(response.accessToken);

      // Clear active role state
      setActiveRole(null);

      // Schedule token refresh with the new (non-elevated) access token
      scheduleTokenRefresh(response.accessToken);

      console.log('[Auth] Role session deactivated');
    } catch (error) {
      console.error('[Auth] Failed to deactivate role session:', error);
      throw error;
    }
  }, [tenant, scheduleTokenRefresh]);

  /**
   * Verify role activation with 2FA code
   * @param code - The verification code entered by the user
   */
  const verifyRoleActivation2FA = useCallback(async (code: string) => {
    if (!pendingRoleActivation2FA) {
      throw new Error('No pending role activation');
    }

    // Call activateRole with the stored durationHours, codeId, and the entered code
    await activateRole(
      pendingRoleActivation2FA.tenantRoleId,
      pendingRoleActivation2FA.durationHours,
      pendingRoleActivation2FA.codeId,
      code
    );
  }, [pendingRoleActivation2FA, activateRole]);

  /**
   * Cancel pending role activation 2FA
   */
  const cancelRoleActivation2FA = useCallback(() => {
    setPendingRoleActivation2FA(null);
  }, []);

  // ============================================================================
  // ROLE SESSION EXPIRED METHODS
  // ============================================================================

  /**
   * Dismiss the role session expired modal.
   * Deactivates the role on the server to get a clean token without role permissions.
   */
  const dismissRoleSessionExpired = useCallback(async () => {
    setExpiredRoleInfo(null);

    // Deactivate on server to get a clean access token
    if (tenant) {
      try {
        const response = await authService.deactivateRoleSession(tenant.tenantId);
        saveAccessToken(response.accessToken);
        setToken(response.accessToken);
        scheduleTokenRefresh(response.accessToken);
      } catch {
        // If deactivation fails (e.g., session already expired server-side),
        // the next token refresh will handle it
      }
    }
  }, [tenant, scheduleTokenRefresh]);

  /**
   * Extend the expired role session.
   * Triggers activateRole which will require 2FA verification.
   */
  const extendRoleSession = useCallback(async () => {
    if (!expiredRoleInfo) return;

    const roleId = expiredRoleInfo.roleId;

    // Clear the expired modal - the 2FA modal will appear if needed
    setExpiredRoleInfo(null);

    // Attempt to re-activate the role (will trigger 2FA)
    await activateRole(roleId);
  }, [expiredRoleInfo, activateRole]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get user's available tenant-type roles (excluding ketrics-member baseline role)
   */
  const availableRoles = useMemo(() => {
    return (
      user?.roles?.filter(
        (r) => r.type === RoleType.TENANT && r.roleCode !== "ketrics-member"
      ) ?? []
    );
  }, [user?.roles]);

  /**
   * Check if a role session is currently active
   */
  const isRoleSessionActive = useMemo(() => {
    if (!activeRole) {
      return false;
    }
    const expiresAt = new Date(activeRole.expires_at);
    return expiresAt > new Date();
  }, [activeRole]);

  /**
   * Calculate remaining seconds until role session expires
   */
  const roleSessionRemainingSeconds = useMemo(() => {
    if (!activeRole) {
      return null;
    }
    const expiresAt = new Date(activeRole.expires_at);
    const now = new Date();
    const remaining = Math.floor((expiresAt.getTime() - now.getTime()) / 1000);
    return remaining > 0 ? remaining : null;
  }, [activeRole]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Initialize auth on mount
   */
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /**
   * Cleanup timers on unmount
   */
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (roleSessionTimerRef.current) {
        clearTimeout(roleSessionTimerRef.current);
      }
    };
  }, []);

  /**
   * Listen for force-logout events from the API client interceptor.
   * When token refresh fails on a regular API call, the interceptor dispatches
   * this event instead of doing a hard redirect.
   */
  useEffect(() => {
    const handleForceLogout = () => {
      console.log('[Auth] Force-logout event received from API interceptor');
      performLocalLogout();
      authSyncChannel?.postMessage({ type: AUTH_SESSION_REVOKED_EVENT });
    };

    window.addEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForceLogout);

    return () => {
      window.removeEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForceLogout);
    };
  }, [performLocalLogout]);

  /**
   * Cross-tab auth sync via BroadcastChannel.
   * TOKEN_REFRESHED: another tab refreshed the tokens — update React state from
   *   localStorage so this tab uses the new access token without re-refreshing.
   * SESSION_REVOKED: another tab logged out — perform local logout here too.
   */
  useEffect(() => {
    if (!authSyncChannel) return;

    const handleAuthSyncMessage = (event: MessageEvent) => {
      if (event.data.type === AUTH_TOKEN_REFRESHED_EVENT) {
        const newToken = getAccessToken();
        if (newToken) {
          setToken(newToken);
          scheduleTokenRefresh(newToken);
          console.log('[Auth] Token synced from another tab');
        }
      } else if (event.data.type === AUTH_SESSION_REVOKED_EVENT) {
        console.log('[Auth] Session revoked by another tab, logging out');
        performLocalLogout();
      }
    };

    authSyncChannel.addEventListener('message', handleAuthSyncMessage);
    return () => {
      authSyncChannel?.removeEventListener('message', handleAuthSyncMessage);
    };
  }, [scheduleTokenRefresh, performLocalLogout]);

  /**
   * Handle visibility change - check token when tab becomes visible
   * This handles the case where setTimeout was throttled in background tabs
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && token) {
        // Check if token is expiring soon or expired
        if (isTokenExpired(token) || getTimeUntilExpiry(token) <= TOKEN_EXPIRY_BUFFER) {
          console.log('[Auth] Tab became visible, token expired or expiring soon, refreshing...');

          const refreshToken = getRefreshToken();
          if (refreshToken) {
            const newToken = await performTokenRefresh();
            if (newToken) {
              setToken(newToken);
              scheduleTokenRefresh(newToken);
              console.log('[Auth] Token refreshed on visibility change');
            } else {
              console.error('[Auth] Token refresh failed on visibility change');
              // Check if refresh token is gone - perform immediate logout
              if (!getRefreshToken()) {
                performLocalLogout();
              }
            }
          } else {
            // No refresh token available - perform local logout
            performLocalLogout();
          }
        } else {
          // Token still valid, but reschedule refresh in case setTimeout was throttled
          scheduleTokenRefresh(token);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, scheduleTokenRefresh, performLocalLogout]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: AuthContextValue = {
    user,
    tenant,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    pendingLogin2FA,
    activeRole,
    isRoleSessionActive,
    roleSessionRemainingSeconds,
    pendingRoleActivation2FA,
    expiredRoleInfo,
    availableRoles,
    loginStep1,
    verifyLogin,
    resendLoginCode,
    cancelLogin2FA,
    completeSSOLogin: completeLogin,
    logout,
    refreshAuth,
    updateUser,
    updateTenant,
    activateRole,
    deactivateRole,
    verifyRoleActivation2FA,
    cancelRoleActivation2FA,
    dismissRoleSessionExpired,
    extendRoleSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * useAuth hook for consuming auth context
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
